import axios from "axios";
import { SERVER_URL } from "./config";
import { currentLanguage, offeredLanguages } from "./i18n";
import { localizeReplyBody } from "./i18n/serverMessages";

const instance = axios.create({
  baseURL: `${SERVER_URL}/api`,
  withCredentials: true,
});

// The language this screen shows, on every call: the server saves it on the
// talent (GET /auth/user, sign-up) and on each fan order, so pushes and
// emails speak it (server lib/i18n). Set once — the language is fixed until
// the app reloads (src/i18n). Only where a choice exists — every build from
// 1.5.2 (the English-only 1.5.1 app never sent it, so it could not overwrite
// a language picked on the website).
if (offeredLanguages().length > 1) instance.defaults.headers.common["X-Rq-Lang"] = currentLanguage();

let accessToken: string | null = null;

// Subscribers that want to react to access-token changes from OUTSIDE
// the React tree (e.g. the desktop companion's usePendingCount hook,
// which lives above App's AuthProvider and therefore can't read
// AuthContext directly). The set is intentionally module-level so the
// hook can register a callback without any provider wiring.
const accessTokenListeners = new Set<(token: string | null) => void>();

export function onAccessTokenChange(
  fn: (token: string | null) => void
): () => void {
  accessTokenListeners.add(fn);
  return () => accessTokenListeners.delete(fn);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null) {
  accessToken = token;
  rememberTokenClock(token);
  if (token) {
    instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete instance.defaults.headers.common.Authorization;
  }
  // Fan out to any subscribers (current implementation: usePendingCount).
  // Errors in one listener shouldn't block the others.
  accessTokenListeners.forEach((fn) => {
    try {
      fn(token);
    } catch {
      /* swallow — a buggy subscriber shouldn't break auth */
    }
  });
}

let csrfInFlight: Promise<void> | null = null;
let hasCsrf = false;
let csrfFetchedAt = 0;
const CSRF_MAX_AGE = 10 * 60 * 1000; // 10 minutes

async function fetchCsrfOnce(): Promise<void> {
  const { data } = await instance.get("/csrf-token");
  instance.defaults.headers.common["X-CSRF-Token"] = data.csrfToken;
  hasCsrf = true;
  csrfFetchedAt = Date.now();
}

export function getCsrf(force = false): Promise<void> {
  const stale = hasCsrf && (Date.now() - csrfFetchedAt > CSRF_MAX_AGE);
  if (hasCsrf && !force && !stale) return Promise.resolve();
  hasCsrf = false;
  if (!csrfInFlight) {
    csrfInFlight = fetchCsrfOnce()
      .catch(() => {})
      .finally(() => {
        csrfInFlight = null;
      });
  }
  return csrfInFlight || Promise.resolve();
}

// axios merges `defaults.headers` into a request's config BEFORE interceptors
// run, so a token fetched inside an interceptor lands in the defaults for
// FUTURE requests only. The request in hand has to be given it explicitly —
// the same fix apiAdmin.ts has carried since its first-write bug.
function stampCsrf(config: any) {
  const fresh = instance.defaults.headers.common["X-CSRF-Token"];
  if (fresh) {
    config.headers = config.headers || {};
    config.headers["X-CSRF-Token"] = fresh;
  }
}

// The access token lives 15 minutes (server utils/tokens.js) and is otherwise
// refreshed only on a 401 (below). A public route that also answers its owner
// never sends that 401: GET /users/:slug, given an expired token, quietly
// answers as if a fan had asked — no owner-only fields, no practice cards
// (founder 2026-10-09: the practice card vanished on the way back to
// Requests). So a request about to carry an expired token refreshes it first.
// Not the refresh itself or its CSRF fetch (they would wait on each other),
// and not fan-rail calls, which carry the fan's own token (lib/fanAccount).
const EXPIRY_MARGIN_MS = 30_000;

function tokenPayload(token: string | null): any {
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

// The phone's clock is not the server's (2026-10-10): one running 15 minutes
// fast saw every token as expired and refreshed before nearly every request
// (each refresh rotates the cookie: a lost race signs the talent out), and a
// slow one never refreshed at all. A token is fresh from the server when it
// arrives, so its `iat` is the server's "now" at that moment; the difference
// is this device's offset, remembered per token (0 when unknown).
const clockOffsetMs = new Map<string, number>();

export function rememberTokenClock(token: string | null) {
  const iat = tokenPayload(token)?.iat;
  if (!token || typeof iat !== "number" || clockOffsetMs.has(token)) return;
  clockOffsetMs.set(token, Date.now() - iat * 1000);
  // Only the current talent and fan tokens matter; forget the oldest.
  if (clockOffsetMs.size > 8) clockOffsetMs.delete(clockOffsetMs.keys().next().value);
}

export function tokenExpiresSoon(token: string | null, now = Date.now()): boolean {
  const exp = tokenPayload(token)?.exp;
  if (typeof exp !== "number") return false;
  const serverNow = now - (clockOffsetMs.get(token as string) ?? 0);
  return exp * 1000 - serverNow < EXPIRY_MARGIN_MS;
}

/** Whose sign-in this is: the user id inside the talent's access token (null when signed out). */
export function accessTokenSubject(): string | null {
  const sub = tokenPayload(accessToken)?.sub;
  return typeof sub === "string" ? sub : null;
}

// One try per expired token when the SERVER said no (the session is over):
// later requests go as they are instead of each waiting on a refresh. A
// refresh that never reached the server (offline, venue Wi-Fi) is not an
// answer, so the next request tries again (2026-10-10: latching on it left
// every later profile read with the fan view); the one refresh in flight
// keeps that from becoming a storm.
let gaveUpOn: string | null = null;

async function freshenAccessToken(config: any) {
  const url = config.url || "";
  if (url === "/csrf-token" || url.includes("/auth/refresh-token") || url.startsWith("/fans/")) return;
  const stale = accessToken;
  if (!tokenExpiresSoon(stale) || stale === gaveUpOn) return;
  try {
    const fresh = await refreshAccessToken();
    setAccessToken(fresh);
    // The stale token is already merged into this request (see stampCsrf);
    // swap it, but never put the talent's token where it wasn't.
    if (config.headers?.Authorization === `Bearer ${stale}`) config.headers.Authorization = `Bearer ${fresh}`;
  } catch (err: any) {
    if (err?.response) gaveUpOn = stale; // the request goes as it is; the 401 retry below still applies
  }
}

instance.interceptors.request.use(async (config) => {
  await freshenAccessToken(config);
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    await getCsrf();
    stampCsrf(config);
  }
  return config;
});

// Retry on CSRF token errors
instance.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original: any = error.config || {};
    if (
      error.response?.status === 403 &&
      error.response?.data?.msg === "Invalid CSRF token" &&
      !original._csrfRetry
    ) {
      original._csrfRetry = true;
      await getCsrf(true);
      // Without this the retry re-sent the token that had just been refused
      // (measured 2026-09-17: the one-shot retry had never succeeded).
      stampCsrf(original);
      return instance(original);
    }
    throw error;
  }
);


// One refresh in flight, app-wide. The server rotates the refresh cookie on
// every call, so two refreshes sent with the same cookie race: one can be told
// "invalid", and whichever caller hears that signs the user out. Everyone who
// needs a refresh at the same moment — AuthContext's check on app open (its
// provider is mounted twice: index.tsx and App.tsx) and the 401 retry below —
// shares the request in flight.
let refreshInFlight: Promise<string> | null = null;

export function refreshAccessToken(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = instance
      .post<{ accessToken: string }>("/auth/refresh-token")
      .then((r) => r.data.accessToken)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

let refreshing = false;
let pending: Array<(t: string | null) => void> = [];

function onRefreshed(token: string | null) {
  pending.forEach((cb) => cb(token));
  pending = [];
}

instance.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original: any = error.config || {};
    if (
      error.response?.status === 401 &&
      !original._retry &&
      !(original.url || "").includes("/auth/refresh-token") &&
      // Fan-rail calls never mean "the DJ session lapsed" — lib/fanAccount
      // owns their 401s (fan refresh + retry) with its own interceptor.
      !(original.url || "").startsWith("/fans/")
    ) {
      original._retry = true;
      if (refreshing) {
        return new Promise((resolve, reject) => {
          pending.push((t) => {
            if (!t) return reject(error);
            original.headers = original.headers || {};
            original.headers.Authorization = "Bearer " + t;
            resolve(instance(original));
          });
        });
      }
      refreshing = true;
      try {
        const newAccess = await refreshAccessToken();
        setAccessToken(newAccess);
        onRefreshed(newAccess);
        original.headers = original.headers || {};
        original.headers.Authorization = "Bearer " + newAccess;
        return instance(original);
      } catch (e) {
        setAccessToken(null);
        onRefreshed(null);
        throw e;
      } finally {
        refreshing = false;
      }
    }
    throw error;
  }
);

// Last: the server's English msg in the viewer's language, on success and on
// error, after the retries above have seen the English (src/i18n/serverMessages).
instance.interceptors.response.use(
  (r) => {
    localizeReplyBody(r?.data);
    return r;
  },
  (error) => {
    localizeReplyBody(error?.response?.data);
    throw error;
  }
);

export default instance;
